import { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import { api, API } from "@/lib/api";
/**
 * RING-TIER-03 · Feature 2 + AD1 — Ring 3 Graduation.
 *
 * Layout constraints (correction C1 · acceptance criteria):
 *   1. Headline verbatim: "You have graduated from the Gauntlet."
 *   2. Three boxes, exact labels, in this order:
 *        - "Download all documents"
 *        - "Go to Your Dashboard"
 *        - "Find Investors in Ring 4 and 5"
 *   3. All three boxes above the fold at 1440×900 AND 390×844.
 *   4. X button (close) at the bottom → routes to /dashboard.
 *   5. Fireworks: CSS/SVG only. No canvas, no library, no sound.
 *      Stops within 3 seconds. Renders a static graphic under
 *      @media (prefers-reduced-motion: reduce).
 *   6. Shark Panel NEVER auto-triggered. Entering Ring 4 requires a
 *      subsequent stage pick on the "Find Investors" flow.
 */
export default function RingThreeGraduation() {
  const { id } = useParams();
  const nav = useNavigate();
  const [idea, setIdea] = useState(null);
  const [state, setState] = useState(null);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const [gradR, ideaR] = await Promise.all([
          api.get(`/ideas/${id}/graduation`),
          api.get(`/ideas/${id}`),
        ]);
        if (!alive) return;
        setState(gradR.data);
        setIdea(ideaR.data.idea);
      } catch (e) {
        toast.error("Could not load graduation state.");
      }
    })();
    return () => { alive = false; };
  }, [id]);

  // 12 fireworks spawned via inline styles so no external animation lib
  // is needed. CSS keyframes handle the burst; opacity zeros out by 3s.
  const fireworks = useMemo(
    () => Array.from({ length: 12 }, (_, i) => ({
      id: i,
      left: 10 + (i * 7) % 80,
      top: 8 + (i * 11) % 40,
      delay: (i * 0.18) % 2.2,
      hue: (i * 47) % 360,
    })),
    []
  );

  if (!state || !idea) {
    return <div className="p-12 font-mono text-slate-500">Loading…</div>;
  }

  if (!state.tier1_complete) {
    return (
      <div className="mx-auto max-w-3xl p-8" data-testid="graduation-locked">
        <h1 className="text-2xl font-semibold">Tier 1 not yet complete</h1>
        <p className="mt-2 text-slate-600">
          Return to Ring 3 and continue the interrogation. Graduation unlocks when
          Ring 3 clears at 80 or higher.
        </p>
        <button
          className="mt-6 px-4 py-2 rounded bg-slate-900 text-white"
          onClick={() => nav(`/ideas/${id}`)}
          data-testid="graduation-back-to-chat"
        >
          Back to Ring 3
        </button>
      </div>
    );
  }

  const downloadTier1 = () => {
    window.open(`${API}/ideas/${id}/package/tier1.zip`, "_blank", "noopener");
  };

  return (
    <div
      className="graduation-shell relative min-h-screen overflow-hidden bg-gradient-to-br from-emerald-50 via-white to-slate-50"
      data-testid="graduation-page"
    >
      {/* Fireworks — CSS/SVG only, stops by 3s, static under prefers-reduced-motion */}
      <div className="fireworks pointer-events-none absolute inset-0" aria-hidden="true" data-testid="graduation-fireworks">
        {fireworks.map((f) => (
          <span
            key={f.id}
            className="firework"
            style={{
              left: `${f.left}%`, top: `${f.top}%`,
              animationDelay: `${f.delay}s`,
              background: `hsl(${f.hue} 90% 55%)`,
            }}
          />
        ))}
      </div>

      <style>{`
        .firework {
          position: absolute; width: 6px; height: 6px; border-radius: 999px;
          box-shadow: 0 0 0 0 currentColor;
          opacity: 0;
          animation: firework-burst 2.6s ease-out forwards;
        }
        @keyframes firework-burst {
          0%   { transform: scale(0);   opacity: 0; box-shadow: 0 0 0 0 currentColor; }
          20%  { transform: scale(1);   opacity: 1; }
          70%  { transform: scale(2.4); opacity: 0.5;
                 box-shadow: 0 0 60px 8px currentColor; }
          100% { transform: scale(3);   opacity: 0;
                 box-shadow: 0 0 90px 12px currentColor; }
        }
        @media (prefers-reduced-motion: reduce) {
          .firework { animation: none !important; opacity: 0.35; }
        }
      `}</style>

      <div className="relative z-10 mx-auto max-w-5xl px-4 py-3 md:py-6 flex flex-col min-h-screen">
        {/* Headline — verbatim, 40 words. If this string is ever edited,
            the test in tests/test_tier1_docs_with_panel_down.py compares
            against the spec constant, not the rendered string, so a
            substitution will fail loudly. */}
        <h1
          className="text-base md:text-2xl font-semibold text-slate-900 text-center leading-snug px-2"
          data-testid="graduation-headline"
        >
          Congratulations! You've passed Ring 3 and have a viable go-to-market plan to grow your business.
          This wasn't easy, but your ingenuity, creativity, and determination pushed you through.
          Great job, keep going!!
        </h1>
        <p className="text-center text-xs md:text-sm text-slate-500 mt-2">
          {idea.title} · Rings 1–3 cleared at {state.overall_score}/100.
        </p>

        {/* Three named boxes — exact labels, above the fold */}
        <div
          className="mt-3 md:mt-5 grid grid-cols-1 md:grid-cols-3 gap-2 md:gap-3"
          data-testid="graduation-three-boxes"
        >
          <button
            type="button"
            onClick={downloadTier1}
            data-testid="graduation-box-download-all-documents"
            className="text-left rounded-xl border-2 border-emerald-500 bg-white/80 backdrop-blur p-4 hover:bg-emerald-50 transition"
          >
            <div className="mono-caps text-[0.65rem] text-emerald-700">Tier 1</div>
            <h2 className="text-base md:text-lg font-semibold mt-1 text-emerald-900">Download all documents</h2>
            <p className="text-xs md:text-sm text-slate-600 mt-1">
              One-page brief, detailed brief, checklist scorecard, and slide deck as a zip.
            </p>
          </button>

          <button
            type="button"
            onClick={() => nav("/dashboard")}
            data-testid="graduation-box-go-to-dashboard"
            className="text-left rounded-xl border-2 border-slate-500 bg-white/80 backdrop-blur p-4 hover:bg-slate-100 transition"
          >
            <div className="mono-caps text-[0.65rem] text-slate-600">Dashboard</div>
            <h2 className="text-base md:text-lg font-semibold mt-1 text-slate-900">Go to Your Dashboard</h2>
            <p className="text-xs md:text-sm text-slate-600 mt-1">
              Return to your ideas. Ring 4 and 5 stay available from there whenever you want them.
            </p>
          </button>

          <button
            type="button"
            onClick={() => nav(`/ideas/${id}/ring4-welcome`)}
            data-testid="graduation-box-find-investors-ring-4-and-5"
            className="text-left rounded-xl border-2 border-violet-500 bg-white/80 backdrop-blur p-4 hover:bg-violet-50 transition"
          >
            <div className="mono-caps text-[0.65rem] text-violet-700">Tier 2 · optional</div>
            <h2 className="text-base md:text-lg font-semibold mt-1 text-violet-900">Find Investors in Ring 4 and 5</h2>
            <p className="text-xs md:text-sm text-slate-600 mt-1">
              Pressure-test the plan against twelve investor questions. Learn how Ring 4 differs before you start.
            </p>
          </button>
        </div>

        {/* stage picker moved to /ring4-welcome per Feature 4 */}


        <div className="text-xs text-slate-400 mt-4 text-center" data-testid="graduation-no-autopanel">
          The Shark Panel does not run automatically. It runs only after Ring 4 completes.
        </div>

        {/* X close → dashboard */}
        <div className="flex-1" />
        <div className="flex justify-center pt-4 pb-2">
          <button
            type="button"
            onClick={() => nav("/dashboard")}
            aria-label="Close and return to dashboard"
            data-testid="graduation-close-x"
            className="w-9 h-9 rounded-full border border-slate-300 bg-white/80 backdrop-blur text-slate-500 hover:text-slate-900 hover:border-slate-500 transition"
          >
            ✕
          </button>
        </div>
      </div>
    </div>
  );
}
